import { FiChevronDown } from "solid-icons/fi";
import {
  type ComponentProps,
  createEffect,
  createMemo,
  createSignal,
  For,
  Show,
  splitProps,
  untrack,
} from "solid-js";
import { useI18n } from "@/api/i18n";
import { cn } from "@/libs/cn";
import {
  addMonths,
  formatMonthYear,
  formatReadableDate,
  getCalendarDays,
  getWeekdayNames,
  getYearsForPicker,
  isDateInRange,
  startOfDay,
  subMonths,
} from "@/libs/date";
import { Button } from "./button";
import { resolveInitialViewDate } from "./date-picker-utils";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export type DatePickerProps = Omit<ComponentProps<"div">, "onChange"> & {
  value?: Date | null;
  onChange?: (date: Date) => void;
  placeholder?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  /**
   * Month the calendar opens on when no value is selected (view only — it
   * never fills the field). Clamped to `minDate`/`maxDate`. Omit it to keep
   * the default behaviour of opening on today.
   */
  defaultViewDate?: Date;
  id?: string;
};

export function DatePicker(props: DatePickerProps) {
  const { t } = useI18n();
  const [local, rest] = splitProps(props, [
    "value",
    "onChange",
    "placeholder",
    "disabled",
    "minDate",
    "maxDate",
    "defaultViewDate",
    "id",
    "class",
  ]);

  const resolveViewDate = (value: Date | null | undefined) =>
    resolveInitialViewDate(value, local.defaultViewDate, {
      minDate: local.minDate,
      maxDate: local.maxDate,
    });

  const [open, setOpen] = createSignal(false);
  const [viewDate, setViewDate] = createSignal(resolveViewDate(local.value));
  /** The header toggles the calendar body between the days grid and the year list. */
  const [showYears, setShowYears] = createSignal(false);
  let yearsContainer: HTMLDivElement | undefined;

  createEffect(() => {
    if (open()) {
      setShowYears(false);
      // Only `open` and `value` are tracked (as before this prop existed):
      // bounds are read as a snapshot so a reactive min/max changing while the
      // popover is open never yanks the user's current month.
      const value = local.value;
      setViewDate(untrack(() => resolveViewDate(value)));
    }
  });

  const calendarDays = createMemo(() => {
    const vd = viewDate();
    return getCalendarDays(vd.getFullYear(), vd.getMonth());
  });

  const currentMonth = createMemo(() => viewDate().getMonth());
  const currentYear = createMemo(() => viewDate().getFullYear());

  const weekdayNames = createMemo(() => getWeekdayNames());

  const prevMonth = () => {
    setViewDate((d) => subMonths(d, 1));
  };

  const nextMonth = () => {
    setViewDate((d) => addMonths(d, 1));
  };

  const monthLabel = createMemo(() => formatMonthYear(viewDate()));
  const years = createMemo(() =>
    getYearsForPicker(local.minDate, local.maxDate),
  );

  const onYearChange = (year: number) => {
    const vd = viewDate();
    setViewDate(new Date(year, vd.getMonth(), 1));
    setShowYears(false);
  };

  // Compute which days are disabled in one place so we use a single min/max for the whole month
  const disabledDaysSet = createMemo(() => {
    const vd = viewDate();
    const min = local.minDate != null ? startOfDay(local.minDate) : undefined;
    const max = local.maxDate != null ? startOfDay(local.maxDate) : undefined;
    const days = getCalendarDays(vd.getFullYear(), vd.getMonth());
    const set = new Set<number>();
    for (const day of days) {
      if (day === null) continue;
      const date = startOfDay(new Date(vd.getFullYear(), vd.getMonth(), day));
      if (!isDateInRange(date, min, max)) set.add(day);
    }
    return set;
  });

  const isDateDisabled = (day: number) => disabledDaysSet().has(day);

  const selectDate = (day: number) => {
    const vd = viewDate();
    const date = startOfDay(new Date(vd.getFullYear(), vd.getMonth(), day));
    local.onChange?.(date);
    setOpen(false);
  };

  return (
    <div class={cn("w-full", local.class)} {...rest}>
      <Popover open={open()} onOpenChange={setOpen}>
        <PopoverTrigger
          as={Button}
          variant="input"
          id={local.id}
          disabled={local.disabled}
          class="w-full justify-between font-normal"
        >
          <Show
            when={local.value}
            fallback={
              <span class="text-muted-foreground">
                {local.placeholder ?? t("common.date_picker.placeholder")}
              </span>
            }
          >
            {(date) => <span>{formatReadableDate(date()!)}</span>}
          </Show>
          <FiChevronDown
            class={cn(
              "size-4 opacity-50 transition-transform",
              open() && "rotate-180",
            )}
          />
        </PopoverTrigger>
        {/* A leaf: the calendar opens no other layer, so it may be `topLayer`. */}
        <PopoverContent hideCloseButton topLayer class="w-auto p-0">
          <div class="flex flex-col p-3">
            <div class="flex items-center justify-between gap-2 border-b pb-3">
              <button
                type="button"
                onClick={prevMonth}
                disabled={showYears()}
                class="rounded-md p-1.5 hover:bg-accent focus:outline-hidden focus:ring-[1.5px] focus:ring-ring disabled:pointer-events-none disabled:opacity-30"
                aria-label={t("common.date_picker.prev_month")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden
                >
                  <title>{t("common.date_picker.prev_month")}</title>
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => setShowYears((v) => !v)}
                aria-expanded={showYears()}
                aria-label={t("common.date_picker.select_year")}
                class="flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium capitalize transition-colors hover:bg-accent focus:outline-hidden focus:ring-[1.5px] focus:ring-ring"
              >
                {monthLabel()}
                <FiChevronDown
                  class={cn(
                    "size-3.5 opacity-50 transition-transform",
                    showYears() && "rotate-180",
                  )}
                />
              </button>
              <button
                type="button"
                onClick={nextMonth}
                disabled={showYears()}
                class="rounded-md p-1.5 hover:bg-accent focus:outline-hidden focus:ring-[1.5px] focus:ring-ring disabled:pointer-events-none disabled:opacity-30"
                aria-label={t("common.date_picker.next_month")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden
                >
                  <title>{t("common.date_picker.next_month")}</title>
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </button>
            </div>
            <Show
              when={!showYears()}
              fallback={
                /* w-62 == the days grid (7 × size-8 + 6 × gap-1), so toggling
                   views never resizes the popover. */
                <div
                  ref={yearsContainer}
                  class="relative grid max-h-64 w-62 grid-cols-4 gap-1 overflow-y-auto pt-3"
                >
                  <For each={years()}>
                    {(year) => (
                      <button
                        type="button"
                        ref={(el) => {
                          if (year !== currentYear()) return;
                          // Deferred so the container ref is assigned: centers the
                          // selected year instead of opening on the oldest one.
                          queueMicrotask(() => {
                            if (!yearsContainer) return;
                            yearsContainer.scrollTop =
                              el.offsetTop -
                              yearsContainer.clientHeight / 2 +
                              el.offsetHeight / 2;
                          });
                        }}
                        onClick={() => onYearChange(year)}
                        class="flex h-8 items-center justify-center rounded-md text-sm transition-colors hover:bg-accent focus:outline-none focus:ring-[1.5px] focus:ring-ring"
                        classList={{
                          "bg-primary text-primary-foreground hover:bg-primary/90":
                            year === currentYear(),
                        }}
                      >
                        {year}
                      </button>
                    )}
                  </For>
                </div>
              }
            >
              <div class="grid grid-cols-7 gap-1 pt-3">
                <For each={weekdayNames()}>
                  {(name) => (
                    <div class="flex h-8 items-center justify-center text-xs font-medium text-muted-foreground">
                      {name}
                    </div>
                  )}
                </For>
                <For each={calendarDays()}>
                  {(day) => (
                    <Show when={day !== null} fallback={<div class="h-8" />}>
                      <Show
                        when={isDateDisabled(day!)}
                        fallback={
                          <button
                            type="button"
                            onClick={() => selectDate(day!)}
                            class="flex h-8 w-8 items-center justify-center rounded-md text-sm transition-colors hover:bg-accent focus:outline-none focus:ring-[1.5px] focus:ring-ring"
                            classList={{
                              "bg-primary text-primary-foreground hover:bg-primary/90":
                                !!(
                                  local.value &&
                                  local.value.getDate() === day &&
                                  local.value.getMonth() === currentMonth() &&
                                  local.value.getFullYear() === currentYear()
                                ),
                            }}
                          >
                            {day}
                          </button>
                        }
                      >
                        <button
                          type="button"
                          disabled
                          class="flex h-8 w-8 cursor-not-allowed items-center justify-center rounded-md text-sm opacity-40 transition-colors"
                        >
                          {day}
                        </button>
                      </Show>
                    </Show>
                  )}
                </For>
              </div>
            </Show>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
